import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Lock, LogIn, Loader2, ShieldCheck, AlertCircle } from "lucide-react";
import { NexSysMark } from "./ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "nexsys_gate_token";

export const getGateToken = () => localStorage.getItem(TOKEN_KEY);

const authFetch = (path, opts = {}) => {
  const token = getGateToken();
  return fetch(`${API}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(opts.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
};

export const gateApi = {
  register: (body) => fetch(`${API}/nexsys/gate/register`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
  login: (body) => fetch(`${API}/nexsys/gate/login`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
  recoveryRequest: (email) => fetch(`${API}/nexsys/auth/recovery/request`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email }) }),
  recoveryComplete: (body) => fetch(`${API}/nexsys/auth/recovery/complete`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
  me: () => authFetch("/nexsys/gate/me"),
  heartbeat: () => authFetch("/nexsys/gate/heartbeat", { method: "POST" }),
  logout: () => authFetch("/nexsys/gate/logout", { method: "POST" }),
  sessions: () => authFetch("/nexsys/gate/sessions"),
  kick: (id) => authFetch(`/nexsys/gate/sessions/${id}/kick`, { method: "POST" }),
  pairCreate: () => authFetch("/nexsys/gate/pair/create", { method: "POST" }),
  pairClaim: (code) => fetch(`${API}/nexsys/gate/pair/claim`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ code }) }),
};

const GateCtx = createContext(null);
export const useNexsysGate = () => useContext(GateCtx) || {};

function GateShell({ children }) {
  return (
    <div className="nx-root fixed inset-0 flex items-center justify-center overflow-hidden bg-[#05070f] px-5" data-testid="nexsys-gate">
      <div className="pointer-events-none absolute inset-0 nx-grid-bg opacity-40" />
      <div className="pointer-events-none absolute left-1/2 top-1/3 h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-cyan-500/10 blur-[120px]" />
      <motion.div
        initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 w-full max-w-[420px] rounded-2xl border border-white/10 bg-white/[0.03] p-7 backdrop-blur-xl"
      >
        {children}
      </motion.div>
    </div>
  );
}

function GateScreen({ onIn }) {
  const [mode, setMode] = useState("login"); // login | register
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [invite, setInvite] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const isReg = mode === "register";
  const isForgot = mode === "forgot";

  useEffect(() => {
    const n = localStorage.getItem("nexsys_gate_notice");
    if (n) { setNotice(n); localStorage.removeItem("nexsys_gate_notice"); }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || (!isForgot && !password)) return;
    if (isReg && (!name.trim() || !invite.trim())) return;
    setBusy(true); setError("");
    try {
      if (isForgot) {
        await gateApi.recoveryRequest(email.trim());
        setNotice("If an account exists for that email, a recovery link was sent. It expires in 15 minutes.");
        setMode("login"); setBusy(false); return;
      }
      const r = isReg
        ? await gateApi.register({ invite_code: invite.trim(), name: name.trim(), email: email.trim(), password })
        : await gateApi.login({ email: email.trim(), password });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        let msg = "Sign-in failed. Please check your email and password.";
        if (typeof d.detail === "string") msg = d.detail;
        else if (Array.isArray(d.detail) && d.detail[0]?.msg) msg = d.detail[0].msg;
        setError(msg); setBusy(false); return;
      }
      localStorage.setItem(TOKEN_KEY, d.token);
      window.dispatchEvent(new Event("nexsys-gate-change"));
      onIn({ name: d.name, email: d.email, is_admin: d.is_admin, role: d.role, session_id: d.session_id });
    } catch {
      setError("Network error. Please try again.");
      setBusy(false);
    }
  };

  const swap = () => { setMode(isReg || isForgot ? "login" : "register"); setError(""); };

  return (
    <GateShell>
      <div className="flex flex-col items-center text-center">
        <NexSysMark size={52} />
        <div className="mt-4 font-display text-2xl font-bold tracking-tight">
          <span className="text-white">Nex</span><span className="text-cyan-400">Sys</span>
        </div>
        <div className="mt-1 flex items-center gap-1.5 font-mono text-[10px] tracking-[0.25em] text-cyan-400/70">
          <Lock className="h-3 w-3" /> SECURE COMMAND CENTER
        </div>
        <p className="mt-3 text-[13px] leading-relaxed text-white/50">
          {isReg ? "Create your account with the invite code to access the Command Center." : "Sign in to your account to access the Command Center."}
        </p>
      </div>

      {notice && (
        <div className="mt-5 flex items-center gap-2 rounded-xl border border-amber-400/30 bg-amber-500/10 px-3 py-2.5 text-[12px] text-amber-200" data-testid="gate-notice">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" /> {notice}
        </div>
      )}

      <form onSubmit={submit} className="mt-6 space-y-3">
        {isReg && (
          <input value={invite} onChange={(e) => setInvite(e.target.value)} placeholder="Invite code" autoComplete="off"
            className="w-full rounded-xl border border-white/10 bg-black/30 px-3.5 py-2.5 text-sm text-white placeholder:text-white/30 outline-none focus:border-cyan-400/50" data-testid="gate-invite" />
        )}
        {isReg && (
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" autoComplete="name"
            className="w-full rounded-xl border border-white/10 bg-black/30 px-3.5 py-2.5 text-sm text-white placeholder:text-white/30 outline-none focus:border-cyan-400/50" data-testid="gate-name" />
        )}
        <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Email" autoComplete="email"
          className="w-full rounded-xl border border-white/10 bg-black/30 px-3.5 py-2.5 text-sm text-white placeholder:text-white/30 outline-none focus:border-cyan-400/50" data-testid="gate-email" />
        {!isForgot && (
        <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" placeholder={isReg ? "Choose a password (min 6 chars)" : "Password"} autoComplete={isReg ? "new-password" : "current-password"}
          className="w-full rounded-xl border border-white/10 bg-black/30 px-3.5 py-2.5 text-sm text-white placeholder:text-white/30 outline-none focus:border-cyan-400/50" data-testid="gate-password" />
        )}
        {error && (
          <div className="flex items-center gap-2 rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-2 text-[12px] text-red-300" data-testid="gate-error">
            <AlertCircle className="h-3.5 w-3.5 shrink-0" /> {error}
          </div>
        )}
        <button type="submit" disabled={busy || !email.trim() || (!isForgot && !password) || (isReg && (!name.trim() || !invite.trim()))}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-cyan-500 px-4 py-2.5 text-sm font-semibold text-black transition-opacity hover:bg-cyan-400 disabled:opacity-50" data-testid="gate-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />} {isForgot ? "Send recovery link" : isReg ? "Create account & enter" : "Sign in"}
        </button>
      </form>

      {!isReg && !isForgot && (
        <button onClick={() => { setMode("forgot"); setError(""); }} className="mt-3 w-full text-center text-[12px] text-cyan-300/80 hover:text-cyan-200" data-testid="gate-forgot-link">
          Forgot password?
        </button>
      )}
      <button onClick={swap} className="mt-4 w-full text-center text-[12px] text-white/50 hover:text-cyan-300" data-testid="gate-swap">
        {isReg ? "Already have an account? Sign in" : isForgot ? "Back to sign in" : "Need an account? Register with an invite code"}
      </button>
      <div className="mt-3 flex items-center justify-center gap-1.5 font-mono text-[10px] text-white/30">
        <ShieldCheck className="h-3 w-3" /> Sessions are logged for security
      </div>
    </GateShell>
  );
}

export function NexsysGate({ children }) {
  const [status, setStatus] = useState("checking"); // checking | out | in
  const [identity, setIdentity] = useState(null);

  const clear = useCallback(() => { localStorage.removeItem(TOKEN_KEY); localStorage.removeItem("nexsys_gate_role"); window.dispatchEvent(new Event("nexsys-gate-change")); setIdentity(null); setStatus("out"); }, []);

  useEffect(() => {
    if (!getGateToken()) { setStatus("out"); return; }
    gateApi.me()
      .then(async (r) => { if (r.ok) { const d = await r.json(); localStorage.setItem("nexsys_gate_role", d.role || "member"); setIdentity(d); setStatus("in"); } else clear(); })
      .catch(() => clear());
  }, [clear]);

  // Heartbeat keeps presence fresh AND detects an admin kick (401 → bounced to gate).
  useEffect(() => {
    if (status !== "in") return;
    const beat = async () => { try { const r = await gateApi.heartbeat(); if (r.status === 401) clear(); } catch { /* offline */ } };
    beat();
    const t = setInterval(beat, 20000);
    return () => clearInterval(t);
  }, [status, clear]);

  // Bounce to the gate immediately when the token is cleared elsewhere (e.g. Chatty on a 401),
  // instead of waiting for the next heartbeat.
  useEffect(() => {
    const onChange = () => { if (!getGateToken()) setStatus((s) => (s === "in" ? "out" : s)); };
    window.addEventListener("nexsys-gate-change", onChange);
    return () => window.removeEventListener("nexsys-gate-change", onChange);
  }, []);

  const logout = useCallback(async () => { try { await gateApi.logout(); } catch { /* ignore */ } clear(); }, [clear]);

  if (status === "checking") {
    return <GateShell><div className="flex items-center justify-center gap-3 py-6 text-white/60"><Loader2 className="h-5 w-5 animate-spin text-cyan-300" /> Verifying access…</div></GateShell>;
  }
  if (status === "out") return <GateScreen onIn={(d) => { localStorage.setItem("nexsys_gate_role", d.role || "member"); setIdentity(d); setStatus("in"); }} />;
  return (
    <GateCtx.Provider value={{ identity, logout }}>
      {identity?.role === "viewer" && (
        <div
          data-testid="viewer-mode-badge"
          className="fixed left-1/2 top-2 z-[9999] -translate-x-1/2 rounded-full border border-amber-400/50 bg-amber-500/15 px-4 py-1.5 font-mono text-[10px] tracking-[0.2em] text-amber-300 backdrop-blur-md shadow-lg shadow-black/40"
        >
          DEMO · VIEWER — READ-ONLY ACCESS
        </div>
      )}
      {children}
    </GateCtx.Provider>
  );
}
